import { useCallback, useMemo, useState } from "react"
import { Checkbox, Input } from "@repo/ui"
import { useTranslation } from "react-i18next"
import { Check, Lock } from "@phosphor-icons/react"
import {
  usePostUnitsSettings,
  useSuspenseUnitsSettings,
} from "@/feature/financial-model/ecommerce/cogs/api"
import {
  RowHeader,
  Table,
  TableBody,
  TableCell,
  TableCellContent,
  TableFooter,
  TableHead,
  TableHeader,
  TableHeaderIcons,
  TableRow,
} from "@/components/ui/table.tsx"
import {
  ActionableCheckbox,
  ActionableTableRow,
  TableActionPopover,
  useTableActionPopoverStore,
} from "@/components/shared/tables/popovers.tsx"
import { TooltipInfo, TooltipProvider } from "@/components/ui/tooltip.tsx"
import { TableErrorProvider } from "../../revenues/context/TableErrorContext"
import {
  DirectCostsApproach,
  Unit,
  UnitSettingPostRequest,
} from "@/feature/financial-model/ecommerce/cogs/api/types.ts"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx"
import { TableRowButton } from "@/components/shared/tables"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx"
import { cn, parseInputValue } from "@/lib/utils"
import { useFormatLocalizedNumber } from "@/lib/hooks/useLocaleFormatter"

const ModelingApproachSelect = ({
  unit,
  financialModelId,
  categoryId,
}: {
  unit: Unit
  financialModelId: string
  categoryId: string
}) => {
  const { t } = useTranslation(`cogs`)
  const { data: settings } = useSuspenseUnitsSettings(
    financialModelId,
    categoryId
  )
  const { mutate: updateUnitsSettings, isPending } = usePostUnitsSettings(
    financialModelId,
    categoryId
  )

  const handleUpdate = useCallback(
    (value: DirectCostsApproach) => {
      if (value === unit.modeling_approach) return
      const payload = {
        level_of_granularity: settings.level_of_granularity,
        category_id: categoryId,
        modeling_approach: settings.modeling_approach,
        unit_settings: settings.unit_settings.map((u) => {
          if (u.product_id !== unit.product_id) return u
          return {
            ...u,
            modeling_approach: value,
          }
        }),
      } as UnitSettingPostRequest
      updateUnitsSettings(payload)
    },
    [
      unit,
      settings.level_of_granularity,
      settings.modeling_approach,
      settings.unit_settings,
      categoryId,
      updateUnitsSettings,
    ]
  )
  const approach: Record<DirectCostsApproach, string> = useMemo(
    () => ({
      absolute_value: t(`cogs.set_up.direct_costs.approach.absolute`),
      cost_per_unit: t(`cogs.set_up.direct_costs.approach.x_unit`),
      percentage_of_revenues: t(
        `cogs.set_up.direct_costs.approach.x_percentage`
      ),
    }),
    [t]
  )
  return (
    <Select
      defaultValue={unit.modeling_approach ?? `absolute_value`}
      onValueChange={handleUpdate}
      disabled={isPending}
    >
      <SelectTrigger className="border-none focus-within:ring-transparent">
        <SelectValue placeholder="Select Approach" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="absolute_value">
          {approach.absolute_value}
        </SelectItem>
        <SelectItem value="cost_per_unit">{approach.cost_per_unit}</SelectItem>
        <SelectItem value="percentage_of_revenues">
          {approach.percentage_of_revenues}
        </SelectItem>
      </SelectContent>
    </Select>
  )
}

const PercentageOfUnitsInput = ({
  unit,
  financialModelId,
  categoryId,
}: {
  unit: Unit
  financialModelId: string
  categoryId: string
}) => {
  const formatNumber = useFormatLocalizedNumber({
    style: `percent`,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
  const { data: settings } = useSuspenseUnitsSettings(
    financialModelId,
    categoryId
  )
  const { mutate: updateUnitsSettings, isPending } = usePostUnitsSettings(
    financialModelId,
    categoryId
  )

  const [value, setValue] = useState(formatNumber(unit.percentage_of_units))

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInputValue(e.target.value)
    setValue(parsedValue)
  }, [])

  const handleFocus = useCallback(() => {
    setValue(unit.percentage_of_units?.toString() ?? ``)
  }, [unit.percentage_of_units])

  const handleBlur = useCallback(() => {
    if (!value) {
      if (!unit.percentage_of_units) return
    }
    if (formatNumber(value) === formatNumber(unit.percentage_of_units)) {
      return setValue(formatNumber(unit.percentage_of_units))
    }
    const payload = {
      level_of_granularity: settings.level_of_granularity,
      category_id: categoryId,
      modeling_approach: settings.modeling_approach,
      unit_settings: settings.unit_settings.map((u) => {
        if (u.product_id !== unit.product_id) return u
        return {
          ...u,
          percentage_of_units: value ? parseFloat(value) : null,
        }
      }),
    } as UnitSettingPostRequest
    updateUnitsSettings(payload)
    setValue(formatNumber(value))
  }, [
    value,
    formatNumber,
    unit,
    settings.level_of_granularity,
    settings.modeling_approach,
    settings.unit_settings,
    categoryId,
    updateUnitsSettings,
  ])

  return (
    <Input
      className="table-input h-10 text-left text-sm text-secondary px-2 flex-grow w-full"
      name={`percentage_of_units`}
      value={value ?? ``}
      disabled={isPending}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  )
}

const ManageProductToCategoryPopoverContent = ({
  financialModelId,
  categoryId,
}: {
  financialModelId: string
  categoryId: string
}) => {
  const { mutate: updateUnitsSettings } = usePostUnitsSettings(
    financialModelId,
    categoryId
  )
  const { data: categorySettings } = useSuspenseUnitsSettings(
    financialModelId,
    categoryId
  )

  const { data: fullUnitsSettings } = useSuspenseUnitsSettings(financialModelId)

  return (
    <PopoverContent className="w-full p-0">
      <Command>
        <CommandInput placeholder="Search products..." />
        <CommandList>
          <CommandEmpty>No products found</CommandEmpty>
          <CommandGroup>
            {fullUnitsSettings.unit_settings.map((unit) => (
              <CommandItem
                key={unit.product_id}
                value={unit.product_id}
                onSelect={() => {
                  updateUnitsSettings({
                    level_of_granularity: categorySettings.level_of_granularity,
                    category_id: categoryId,
                    modeling_approach: categorySettings.modeling_approach,
                    unit_settings: categorySettings.unit_settings.find(
                      (cs) => cs.product_id === unit.product_id
                    )
                      ? categorySettings.unit_settings.filter(
                          (cs) => cs.product_id !== unit.product_id
                        )
                      : [
                          ...categorySettings.unit_settings,
                          {
                            product_id: unit.product_id,
                            modeling_approach:
                              categorySettings.level_of_granularity ===
                              `by_product`
                                ? `absolute_value`
                                : null,
                            percentage_of_units: 100,
                          },
                        ],
                  } as UnitSettingPostRequest)
                }}
              >
                <Check
                  className={cn(
                    `mr-2 h-4 w-4`,
                    categorySettings.unit_settings.find(
                      (u) => u.product_id === unit.product_id
                    )
                      ? `opacity-100`
                      : `opacity-0`
                  )}
                />
                {unit.product_name}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  )
}

export const DetailCategoryProductsTable = ({
  categoryId,
  financialModelId,
}: {
  financialModelId: string
  categoryId: string
}) => {
  const { t } = useTranslation(`cogs`)
  const { data: settings } = useSuspenseUnitsSettings(
    financialModelId,
    categoryId
  )

  const { mutate: updateUnitsSettings } = usePostUnitsSettings(
    financialModelId,
    categoryId
  )

  const allSelected = useTableActionPopoverStore((state) => state.allSelected)
  const setCheckedProducts = useTableActionPopoverStore(
    (state) => state.setSelected
  )
  const selectedProducts = useTableActionPopoverStore((state) => state.selected)

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setCheckedProducts([])
    } else {
      setCheckedProducts(settings.unit_settings.map((unit) => unit.product_id))
    }
  }, [allSelected, setCheckedProducts, settings.unit_settings])

  const handleDelete = useCallback(() => {
    if (selectedProducts.length === 0) return
    updateUnitsSettings({
      level_of_granularity: settings.level_of_granularity,
      category_id: categoryId,
      modeling_approach: settings.modeling_approach,
      unit_settings: settings.unit_settings.filter(
        (us) => !selectedProducts.includes(us.product_id)
      ),
    } as UnitSettingPostRequest)
  }, [
    categoryId,
    selectedProducts,
    settings.level_of_granularity,
    settings.modeling_approach,
    settings.unit_settings,
    updateUnitsSettings,
  ])
  if (!settings.level_of_granularity) {
    return null
  }
  const settingsModellingApproach =
    settings.level_of_granularity === `by_product`
      ? `cost_per_unit`
      : (settings.modeling_approach ?? undefined)
  if (settingsModellingApproach !== `cost_per_unit`) {
    return null
  }
  return (
    <TooltipProvider>
      <TableErrorProvider>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="flex items-center max-w-6">
                <TableActionPopover
                  onDelete={handleDelete}
                  maxItems={settings.unit_settings.length}
                >
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                  />
                </TableActionPopover>
              </TableHead>
              <TableHead>
                <TableHead>
                  {t(
                    `cogs.set_up.direct_costs.detailed.products.table_head.products`
                  )}
                </TableHead>
              </TableHead>
              {settings.level_of_granularity === `by_product` && (
                <TableHead className="text-left">
                  <span className="inline-flex items-center gap-2">
                    <TableHeaderIcons icon="options" />
                    {t(
                      `cogs.set_up.direct_costs.detailed.products.table_head.modelling_approach`
                    )}
                    <TooltipInfo>
                      {t(
                        `cogs.set_up.direct_costs.detailed.products.table_head.modelling_approach_tooltip`
                      )}
                    </TooltipInfo>
                  </span>
                </TableHead>
              )}
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  {t(
                    `cogs.set_up.direct_costs.detailed.products.table_head.of_units`
                  )}
                  <TooltipInfo>
                    {t(
                      `cogs.set_up.direct_costs.detailed.products.table_head.of_units_tooltip`
                    )}
                  </TooltipInfo>
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settings.unit_settings.map((unit) => (
              <ActionableTableRow key={unit.product_id} id={unit.product_id}>
                <TableCell role="checkbox">
                  <ActionableCheckbox />
                </TableCell>
                <TableCell>
                  <RowHeader direction="horizontal">
                    {unit.product_name}
                  </RowHeader>
                </TableCell>
                {settings.level_of_granularity === `by_product` && (
                  <TableCell>
                    <ModelingApproachSelect
                      unit={unit}
                      financialModelId={financialModelId}
                      categoryId={categoryId}
                    />
                  </TableCell>
                )}
                <TableCell
                  readonly={
                    (settings.level_of_granularity === `by_product` &&
                      unit.modeling_approach !== `cost_per_unit`) ||
                    (settings.level_of_granularity === `aggregate` &&
                      settings.modeling_approach !== `cost_per_unit`)
                  }
                >
                  <TableCellContent className="max-w-none w-full justify-start px-2">
                    {(settings.level_of_granularity === `by_product` &&
                      unit.modeling_approach !== `cost_per_unit`) ||
                    (settings.level_of_granularity === `aggregate` &&
                      settings.modeling_approach !== `cost_per_unit`) ? (
                      <span className="w-full h-full flex items-center justify-between">
                        <span className="text-disabled">-</span>
                        <Lock size={16} className="text-disabled" />
                      </span>
                    ) : (
                      <PercentageOfUnitsInput
                        unit={unit}
                        financialModelId={financialModelId}
                        categoryId={categoryId}
                      />
                    )}
                  </TableCellContent>
                </TableCell>
              </ActionableTableRow>
            ))}
          </TableBody>
          <TableFooter>
            <Popover>
              <PopoverTrigger asChild>
                <TableRowButton
                  className="w-full justify-start"
                  span={4}
                  onClick={() => {}}
                />
              </PopoverTrigger>
              <ManageProductToCategoryPopoverContent
                categoryId={categoryId}
                financialModelId={financialModelId}
              />
            </Popover>
          </TableFooter>
        </Table>
      </TableErrorProvider>
    </TooltipProvider>
  )
}
